// SiteFooter — global Nexus-themed footer with legal links, wallet & payment
// notes, and social touches. Rendered on every route EXCEPT immersive views
// (watch/live/feed/messages/DMs/video calls) so it doesn't fight the chat
// overlay, stickers, or a live camera. All links point to existing pages;
// no policy text is added here. Feb 2026.
import { Link, useLocation } from "react-router-dom";
import { Shield, ScrollText, Users, MessageCircle } from "lucide-react";

const YEAR = new Date().getFullYear();

export default function SiteFooter() {
  const loc = useLocation();
  // Immersive routes where the floating chat/stickers/video-call live — do NOT
  // paint a bottom footer there or it would push the composer up / crash into
  // the live chat rail.
  const EXCLUDE = ["/watch", "/feed", "/messages", "/dm", "/studio/tools"];
  if (EXCLUDE.some((p) => loc.pathname.startsWith(p))) return null;

  const isLanding = loc.pathname === "/";

  return (
    <footer
      className={`mt-16 border-t ${isLanding ? "border-white/10 bg-transparent text-white/60" : "border-black/10 bg-white text-nexus-obsidian/70"}`}
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid gap-8 md:grid-cols-4">
        {/* Wordmark + one-liner */}
        <div className="md:col-span-2 space-y-3">
          <div className="font-display font-black tracking-[0.28em] text-lg" style={isLanding ? { color: "#fff" } : { color: "#0a0a0a" }}>
            NEXUS
          </div>
          <p className="text-xs leading-relaxed max-w-md">
            Live streaming, short-form Reels, and creator monetisation — engineered in Australia by <span className="font-semibold">ELYSIUM Labs</span>. Every stream, every tip, every reel — your economy.
          </p>
        </div>

        {/* Legal column */}
        <div className="space-y-2">
          <div className="label-eyebrow text-[10px] tracking-[0.3em] uppercase" style={isLanding ? { color: "rgba(255,255,255,0.45)" } : { color: "rgba(0,0,0,0.4)" }}>
            // legal
          </div>
          <ul className="space-y-1.5 text-xs">
            <li><Link to="/legal/terms" className="inline-flex items-center gap-1.5 hover:text-nexus-cyan transition" data-testid="footer-link-terms"><ScrollText className="w-3 h-3" /> Terms &amp; Conditions</Link></li>
            <li><Link to="/legal/privacy" className="inline-flex items-center gap-1.5 hover:text-nexus-cyan transition" data-testid="footer-link-privacy"><Shield className="w-3 h-3" /> Privacy Policy</Link></li>
            <li><Link to="/legal/community" className="inline-flex items-center gap-1.5 hover:text-nexus-cyan transition" data-testid="footer-link-community"><Users className="w-3 h-3" /> Community Guidelines</Link></li>
          </ul>
        </div>

        {/* Support / Contact column */}
        <div className="space-y-2">
          <div className="label-eyebrow text-[10px] tracking-[0.3em] uppercase" style={isLanding ? { color: "rgba(255,255,255,0.45)" } : { color: "rgba(0,0,0,0.4)" }}>
            // support
          </div>
          <ul className="space-y-1.5 text-xs">
            <li><a href="mailto:kaye@elysium.nexus" className="inline-flex items-center gap-1.5 hover:text-nexus-cyan transition" data-testid="footer-link-contact"><MessageCircle className="w-3 h-3" /> kaye@elysium.nexus</a></li>
            <li><Link to="/pricing" className="hover:text-nexus-cyan transition" data-testid="footer-link-pricing">Pricing &amp; NX bundles</Link></li>
            <li><Link to="/parental" className="hover:text-nexus-cyan transition" data-testid="footer-link-parental">Parental controls</Link></li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className={`border-t ${isLanding ? "border-white/10" : "border-black/10"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-3 text-[10px] tracking-[0.28em] uppercase font-display">
          <div style={isLanding ? { color: "rgba(255,255,255,0.55)" } : { color: "rgba(0,0,0,0.5)" }} data-testid="footer-copyright">
            © {YEAR} Nexus by ELYSIUM — Sydney · Melbourne · Brisbane
          </div>
          <div className="flex gap-4 text-[10px]" style={isLanding ? { color: "rgba(255,255,255,0.45)" } : { color: "rgba(0,0,0,0.4)" }}>
            <span>AUD Native</span>
            <span>·</span>
            <span>PayPal Secured</span>
            <span>·</span>
            <Link to="/legal/terms" className="hover:text-nexus-cyan transition">v3.9</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
